import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

function Answer({ it, v, set, disabled }) {
  const pair = it.type === "yes_no" ? ["Yes", "No"] : ["Pass", "Fail"];
  if (it.type === "checkbox") return <Checkbox checked={!!v} onCheckedChange={(x) => set(!!x)} disabled={disabled} data-testid={`fill-${it.id}`} />;
  if (["yes_no", "pass_fail"].includes(it.type)) return (
    <div className="flex gap-2">{pair.map((p) => <Button key={p} type="button" size="sm" variant={v === p ? "default" : "outline"} disabled={disabled} onClick={() => set(p)} data-testid={`fill-${it.id}-${p.toLowerCase()}`}>{p}</Button>)}</div>
  );
  if (it.type === "dropdown") return (
    <Select value={v || ""} onValueChange={set} disabled={disabled}>
      <SelectTrigger className="h-10 max-w-sm" data-testid={`fill-${it.id}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
      <SelectContent>{(it.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  if (it.type === "multiple_choice") return (
    <div className="flex flex-wrap gap-3" data-testid={`fill-${it.id}`}>{(it.options || []).map((o) => (
      <label key={o} className="flex items-center gap-1.5 text-sm"><Checkbox checked={(v || []).includes(o)} disabled={disabled}
        onCheckedChange={(x) => set(x ? [...(v || []), o] : (v || []).filter((y) => y !== o))} />{o}</label>
    ))}</div>
  );
  if (it.type === "long_text") return <Textarea value={v || ""} onChange={(e) => set(e.target.value)} disabled={disabled} rows={3} data-testid={`fill-${it.id}`} />;
  if (it.type === "photo") return <p className="text-sm text-slate-400">Photos are added from the mobile app.</p>;
  const type = { number: "number", date: "date", time: "time", datetime: "datetime-local" }[it.type] || "text";
  return <Input type={type} value={v || ""} onChange={(e) => set(e.target.value)} disabled={disabled} className="h-10 max-w-sm" placeholder={it.type === "signature" ? "Type full name to sign" : ""} data-testid={`fill-${it.id}`} />;
}

// Fill in / view a job checklist from the portal.
export const ChecklistFill = ({ inst, onOpenChange, onSaved, preview = false }) => {
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAnswers(inst?.answers || {}); }, [inst]);
  if (!inst) return null;
  const done = inst.status === "submitted";
  const send = async (submit) => {
    setBusy(true);
    try {
      await client.post(`/forms/instances/${inst.id}/${submit ? "submit" : "answer"}`, { answers });
      toast.success(submit ? "Checklist submitted" : "Progress saved");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!inst} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checklist-fill-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{inst.name}</DialogTitle>
          <DialogDescription>{done ? `Submitted ${inst.submittedBy ? `by ${inst.submittedBy}` : ""}` : inst.enforce ? "Must be completed before the job can be finished." : "Fill in and submit when done."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {(inst.sectionsSnapshot || []).map((s) => (
            <div key={s.id} className="space-y-3">
              {s.title && <p className="font-semibold text-slate-900 border-b pb-1">{s.title}</p>}
              {(s.items || []).map((it) => (
                <div key={it.id} className="space-y-1.5">
                  <p className={`text-sm ${it.type === "info" ? "text-slate-500 italic" : "text-slate-800"}`}>{it.label}{it.required && <span className="text-red-500"> *</span>}</p>
                  {it.type !== "info" && <Answer it={it} v={answers[it.id]} set={(v) => setAnswers((a) => ({ ...a, [it.id]: v }))} disabled={busy} />}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="checklist-fill-close">Close</Button>
          {!preview && <>
          <Button variant="outline" onClick={() => send(false)} disabled={busy} data-testid="checklist-fill-save">Save progress</Button>
          <Button onClick={() => send(true)} disabled={busy} className="bg-brand hover:bg-brand-dark" data-testid="checklist-fill-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : done ? "Update submission" : "Submit"}
          </Button>
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
